import { z } from 'zod/v4';
import { telegramChatTypeEnum as TelegramChatTypeEnum, type TelegramChatType } from '../enums/index';

// ========== Query Schemas ==========

export const TelegramChannelFiltersSchema = z.object({
  isActive: z.string().optional().transform((val) => {
    if (val === 'true') {return true;}
    if (val === 'false') {return false;}
    return;
  }),
  type: TelegramChatTypeEnum.optional(),
  search: z.string().optional(),
  sortBy: z.string().optional(),
  sortOrder: z.string().optional(),
  page: z.coerce.number().int('Страница должна быть целым числом').min(1, 'Страница должна быть >= 1').default(1),
  limit: z.coerce.number().int('Лимит должен быть целым числом').min(1, 'Лимит должен быть >= 1').max(500, 'Лимит должен быть <= 500').default(20),
});

export type TelegramChannelFiltersDto = z.infer<typeof TelegramChannelFiltersSchema>;

// ========== Body Schemas ==========

export const CreateTelegramChannelSchema = z.object({
  telegramId: z.string({ message: 'Telegram ID обязателен' }).transform((val) => Number(val)),
  title: z.string({ message: 'Название обязательно' }).min(1, 'Название обязательно').max(255, 'Название слишком длинное (максимум 255 символов)'),
  type: TelegramChatTypeEnum.default('channel'),
  isActive: z.boolean().default(true),
  username: z.string().optional(),
  description: z.string().max(1000, 'Описание слишком длинное (максимум 1000 символов)').optional(),
  photoUrl: z.string().url('Некорректный URL фото').optional(),
  inviteLink: z.string().url('Некорректный URL приглашения').optional(),
  canSendMessages: z.boolean().default(false),
  canInviteUsers: z.boolean().default(false),
  canRestrictMembers: z.boolean().default(false),
  botStatus: z.string().optional(),
  addedByBotId: z.number().int('ID бота должен быть целым числом').positive('ID бота должен быть положительным').optional(),
});

export type CreateTelegramChannelDto = z.infer<typeof CreateTelegramChannelSchema>;

// Re-export TelegramChatType for backward compatibility
export type { TelegramChatType };

export const UpdateTelegramChannelSchema = z.object({
  title: z.string().min(1, 'Название обязательно').max(255, 'Название слишком длинное (максимум 255 символов)').optional(),
  isActive: z.boolean().optional(),
  type: TelegramChatTypeEnum.optional(),
  username: z.string().optional(),
  description: z.string().max(1000, 'Описание слишком длинное (максимум 1000 символов)').optional(),
  photoUrl: z.string().url('Некорректный URL фото').optional().nullable(),
  inviteLink: z.string().url('Некорректный URL приглашения').optional().nullable(),
  canSendMessages: z.boolean().optional(),
  canInviteUsers: z.boolean().optional(),
  canRestrictMembers: z.boolean().optional(),
  botStatus: z.string().optional(),
});

export type UpdateTelegramChannelDto = z.infer<typeof UpdateTelegramChannelSchema>;

// ========== Response Types ==========

export interface TelegramChannelResponse {
  id: number;
  telegramId: string;
  title: string;
  isActive: boolean;
  username?: string | null;
  /** Total member count from Telegram (external) */
  memberCount?: number;
  /** Active members from our system (internal subscriptions) */
  activeMemberCount?: number;
  photoUrl?: string | null;
  type: TelegramChatType;
  description?: string | null;
  lastSyncedAt?: Date | null;
  /** Invite link for private chats/channels */
  inviteLink?: string | null;
  /** Join Request link (creates_join_request: true) for access control */
  joinRequestLink?: string | null;
  /** Can bot send messages (admin rights) */
  canSendMessages: boolean;
  /** Can bot invite users */
  canInviteUsers: boolean;
  /** Can bot restrict/kick members */
  canRestrictMembers: boolean;
  /** Bot status in chat: administrator, member, left, kicked */
  botStatus?: string | null;
  /** Date when bot was added to the chat */
  botAddedAt?: Date | null;
  /** Bot that added this channel */
  addedByBotId?: number | null;
  botName?: string | null;
  createdAt: Date;
  updatedAt: Date;
}
